/**
 * Single source of truth for per-route SEO metadata (title, description,
 * canonical path) and the JSON-LD schema.org markup for each route.
 *
 * Read by BOTH the server (server/_core/vite.ts injects these into the
 * HTML response before it's sent, so a crawler that never executes
 * JavaScript still sees the right metadata for the page it requested —
 * see docs/engineering/backlog/AEO-SEO-DEEP-REVIEW-2026-09.md Finding 0)
 * and the client (client/src/hooks/useSeo.ts re-applies the same values
 * post-hydration as a safety net). Previously each page hardcoded its own
 * title/description inline, which is how it stayed silently un-set for
 * every route except the homepage at the HTTP-response level.
 */

import {
  ADDRESS,
  CANONICAL_DESCRIPTION,
  COMPANY_ALT_NAME,
  COMPANY_NAME,
  EMAIL,
  PHONE_E164,
  SITE_URL,
  TIPS_SHORT_NAME,
} from "./companyFacts";
import { FLEET_PROTECTION_FAQ, RESOURCES_FAQ, TFRSUPPLY_CLAIM_FAQ, TFRSUPPLY_LAUNCH_FAQ } from "./faqData";

export interface RouteSeoMetadata {
  path: string;
  title: string;
  description: string;
  /** JSON-LD object(s) to embed for this route. Omit for utility pages (Privacy/Terms/Quote/Contact/etc.) that don't need their own schema. */
  jsonLd?: object | object[];
  /** Emit `<meta name="robots" content="noindex, follow">` (server-side in injectSeoMetadata and client-side via useSeo) -- for pages reachable only by direct link, like the /claim funnel. Such routes are also left out of public/sitemap.xml and the site menu. */
  noindex?: boolean;
}

const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: COMPANY_NAME,
  alternateName: COMPANY_ALT_NAME,
  url: SITE_URL,
  logo: "https://tx1rs.com/optimized-media/IMG_1932_d2bcd1b1-640.webp",
  image: "https://tx1rs.com/optimized-media/IMG_1932_d2bcd1b1-1024.webp",
  description: CANONICAL_DESCRIPTION,
  telephone: PHONE_E164,
  email: EMAIL,
  address: {
    "@type": "PostalAddress",
    ...ADDRESS,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "18:00",
  },
  sameAs: ["https://www.facebook.com/TexasFirstResponseServices"],
  areaServed: {
    "@type": "State",
    name: "Texas",
  },
};

function serviceSchema(opts: { name: string; description: string; path: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: opts.name,
    name: opts.name,
    description: opts.description,
    provider: {
      "@type": "LocalBusiness",
      name: COMPANY_NAME,
      telephone: PHONE_E164,
      url: SITE_URL,
    },
    areaServed: {
      "@type": "State",
      name: "Texas",
    },
    url: `${SITE_URL}${opts.path}`,
  };
}

export const SEO_ROUTES: RouteSeoMetadata[] = [
  {
    path: "/",
    title: "Texas First Response Services | Emergency Vehicle Upfitter",
    description:
      "SBA 8(a) certified emergency vehicle upfitter for law enforcement, fire/EMS, K9, and commercial fleets across Texas. Six SBA certifications. (832) 225-2838.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/law-enforcement",
    title: "Law Enforcement Upfitting | Texas First Response Services",
    description:
      "Professional police vehicle upfitting in Texas — emergency lighting, comms, and complete patrol vehicle builds. SBA 8(a) certified. Request a quote today.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "Law Enforcement Vehicle Upfitting",
        description:
          "Full-turnkey patrol vehicle upfits: emergency lighting, communications, ballistic protection, prisoner transport, and consoles.",
        path: "/law-enforcement",
      }),
    ],
  },
  {
    path: "/fire-brush-trucks",
    title: "Fire, EMS & Brush Trucks | Texas First Response Services",
    description:
      "Custom fire command vehicles, EMS upfits, and brush truck builds for Texas departments. SBA 8(a) certified emergency vehicle upfitter. Request a quote today.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "Fire, EMS & Brush Truck Upfitting",
        description:
          "Custom fire command vehicles, EMS upfits, and 4x4 wildland brush truck builds for Texas departments.",
        path: "/fire-brush-trucks",
      }),
    ],
  },
  {
    path: "/crew-cab-brush-truck",
    title: "2027 Crew Cab Brush Truck | Texas First Response Services",
    description:
      "Turnkey 2027 crew-cab brush truck on a Ford F-550 or Dodge Ram 5500 platform. Fast Attack 500-gallon system, 360° warning lighting. Starting under $300,000.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "2027 Crew Cab Brush Truck",
        description:
          "Turnkey crew-cab brush truck available on a Ford F-550 or Dodge Ram 5500 platform, with a Fast Attack 500-gallon system and 360° warning lighting.",
        brand: { "@type": "Brand", name: COMPANY_NAME },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: "300000",
          priceSpecification: {
            "@type": "PriceSpecification",
            minPrice: "300000",
            priceCurrency: "USD",
          },
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/crew-cab-brush-truck`,
        },
      },
    ],
  },
  {
    path: "/k9-consult",
    title: "K9 Vehicle Consultation | Texas First Response Services",
    description:
      "Purpose-built K9 transport systems designed around your canine unit's safety and your agency's operational standards. Schedule a K9 vehicle consultation.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "K9 Vehicle Consultation",
        description:
          "Purpose-built K9 transport systems: kennel and partition systems, climate/ventilation controls, and safety equipment on any platform.",
        path: "/k9-consult",
      }),
    ],
  },
  {
    path: "/federal-contracting",
    title: "Federal Contracting | Texas First Response Services",
    description:
      `Texas First Response Services holds six SBA federal certifications, registered on ${TIPS_SHORT_NAME} cooperative contracts for fast government vehicle procurement.`,
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "Government & Federal Contract Vehicle Upfitting",
        description:
          `Sole-source and cooperative-contract (${TIPS_SHORT_NAME}) vehicle upfitting procurement for federal, state, and local agencies, backed by all six SBA certifications (WOSB, EDWOSB, SDVOSB, VOSB, HUBZone, 8(a)).`,
        path: "/federal-contracting",
      }),
    ],
  },
  {
    path: "/commercial",
    title: "Commercial Vehicle Upfitting | Texas First Response Services",
    description:
      "Fleet upfitting and equipment installation for commercial and government contractors — work truck builds and custom vehicle solutions across Texas.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "Commercial & Work Truck Upfitting",
        description:
          "Fleet upfitting and equipment installation for commercial and government contractors — work truck builds and fleet programs.",
        path: "/commercial",
      }),
    ],
  },
  {
    path: "/municipal-fleet-vehicle-protection",
    title: "Municipal Fleet Vehicle Protection | Texas First Response",
    description:
      "A vehicle service contract for city, county, and public-safety fleets — $0 deductible, unlimited mileage/engine hours, upfit equipment coverage.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      serviceSchema({
        name: "Municipal Fleet Vehicle Protection",
        description:
          "A vehicle service contract for municipal fleets — Emergency Vehicle Protection (up to 30 model years) and Commercial Vehicle Protection (up to 20 model years), with zero-dollar deductible options, unlimited mileage/engine hours, and upfit-equipment coverage.",
        path: "/municipal-fleet-vehicle-protection",
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FLEET_PROTECTION_FAQ.map(faq => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ],
  },
  {
    path: "/about",
    title: "About Us | Texas First Response Services",
    description:
      "Veteran-owned, government-certified emergency vehicle upfitter based in Dickinson, TX. Learn about our team and our commitment to those who serve.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/contact",
    title: "Contact Us | Texas Vehicle Upfitter — TFRS",
    description:
      "Call (832) 225-2838 or message our Dickinson, TX shop. SBA 8(a) certified upfitter for law enforcement, fire/EMS, and commercial fleets statewide.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/quote",
    title: "Request a Quote | Texas First Response Services",
    description:
      "Request a quote for emergency vehicle upfitting from Texas First Response Services. Tell us what you need — we respond within 1 business day.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/gallery",
    title: "Project Gallery | Texas First Response Services",
    description:
      "Browse our portfolio of completed vehicle upfitting projects — patrol vehicles, fire command trucks, K9 units, and commercial fleet builds.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/resources",
    title: "Resources & Knowledge Base | Texas First Response Services",
    description:
      "Procurement links, manufacturer documentation, and answers to common questions — everything you need to plan, specify, and procure your next vehicle upfit.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: RESOURCES_FAQ.map(faq => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ],
  },
  {
    path: "/configure",
    title: "Vehicle Configurator | Texas First Response Services",
    description:
      "Build and price your custom emergency vehicle upfit online. Configure lighting, equipment, and accessories for law enforcement, fire/EMS, and commercial fleets.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/supply-store",
    title: "Claim Your Agency's TFRSupply Account",
    description:
      "TFRSupply is an e-commerce platform for government agencies. Claim your agency's account now so it's ready when TFRSupply launches.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: TFRSUPPLY_CLAIM_FAQ.map(faq => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ],
  },
  {
    // Launch-event landing page for an email campaign. Originally excluded
    // from sitemap.xml/llms.txt (same treatment as /admin/analytics) since
    // it was meant only for campaign-email clicks; per explicit maintainer
    // direction it's now indexed for local search, so it's listed in both
    // files and the title was shortened to fit this repo's usual 60-char
    // SERP-snippet guard (was 87 chars, exempted, when the page wasn't
    // indexed -- see git history for that version).
    path: "/federal-signal-launch",
    title: "Federal Signal Wholesale Pricing for Texas First Responders",
    description: "TFRSupply is an e-commerce platform built for Texas first responders.",
    jsonLd: [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: TFRSUPPLY_LAUNCH_FAQ.map(faq => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ],
  },
  {
    path: "/capabilities",
    title: "SBA 8(a) Capabilities | Texas First Response Services",
    description:
      "SBA 8(a), HUBZone, SDVOSB, and WOSB certified. Cooperative contract vehicles, technical capabilities, and agency experience for procurement teams.",
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  },
  {
    path: "/privacy",
    title: "Privacy Policy | Texas First Response Services",
    description:
      "How Texas First Response Services collects, uses, and protects information submitted through tx1rs.com.",
  },
  {
    path: "/accessibility",
    title: "Accessibility Statement | Texas First Response Services",
    description:
      "Texas First Response Services' commitment to WCAG 2.1 AA accessibility on tx1rs.com, and how to request help or report a barrier.",
  },
  {
    path: "/terms",
    title: "Terms of Service | Texas First Response Services",
    description:
      "Terms governing use of tx1rs.com and requests submitted to Texas First Response Services.",
  },
  {
    // Internal live analytics dashboard, gated by auth client-side
    // (client/src/pages/AdminAnalytics.tsx). Title/description mirror that
    // page's own useSeo() call exactly, so the server-side (pre-hydration)
    // and client-side (post-hydration) metadata agree. Was previously
    // missing from this list entirely, so it silently inherited the
    // homepage's title/description and had no server-side noindex tag --
    // see docs/engineering/backlog/AEO-SEO-DEEP-REVIEW-2026-09.md.
    path: "/admin/analytics",
    title: "Analytics | Texas First Response Services",
    description: "Internal live analytics dashboard.",
    noindex: true,
  },
  {
    path: "/claim",
    title: "Claim your account | TFRSupply",
    description: "TFRSupply Wholesale Launch Event agency account steps.",
    noindex: true,
  },
  {
    path: "/claim/details",
    title: "Demographics | TFRSupply",
    description: "TFRSupply Wholesale Launch Event agency account steps.",
    noindex: true,
  },
  {
    path: "/claim/fleet",
    title: "Fleet details | TFRSupply",
    description: "TFRSupply Wholesale Launch Event agency account steps.",
    noindex: true,
  },
  {
    path: "/claim/pickup",
    title: "Finish later | TFRSupply",
    description: "TFRSupply Wholesale Launch Event agency account steps.",
    noindex: true,
  },
];

const SEO_ROUTES_BY_PATH: Record<string, RouteSeoMetadata> = Object.fromEntries(
  SEO_ROUTES.map(route => [route.path, route])
);

export function getSeoMetadata(path: string): RouteSeoMetadata | undefined {
  return SEO_ROUTES_BY_PATH[path];
}
